import React, { useState, useEffect, useMemo, useRef } from 'react';
import { apiClient } from '../services/api';
import { Plus, Send, Search, Trash2, Download, Upload, FileSpreadsheet, Copy, Users, CheckCircle, Clock, Bell, X, ClipboardList } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';

const RFQs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [additionalItems, setAdditionalItems] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [copying, setCopying] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalData, setProgressModalData] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const csvInputRef = useRef(null);
  const [prConversion, setPrConversion] = useState(null); // Track PR being converted
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCreating, setIsCreating] = useState(false); // Prevent multiple submissions
  const [formData, setFormData] = useState({
    item_name: '',
    description: '',
    quantity: '',
    unit: '',
    payment_term: '',
    transport: '',
    link_validity_days: 7,
  });

  // Debounced item name search for autocomplete
  const fetchItemSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setItemSuggestions([]);
      return;
    }
    try {
      const response = await apiClient.get(`/items/suggestions?q=${encodeURIComponent(query)}`);
      setItemSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // Handle item name change with suggestions
  const handleItemNameChange = (value) => {
    setFormData({ ...formData, item_name: value });
    setShowSuggestions(true);
    // Debounce the API call
    clearTimeout(window.itemSuggestTimeout);
    window.itemSuggestTimeout = setTimeout(() => {
      fetchItemSuggestions(value);
    }, 300);
  };

  // Select a suggestion
  const selectSuggestion = (suggestion) => {
    setFormData({
      ...formData,
      item_name: suggestion.item_name,
      description: suggestion.description || formData.description,
      unit: suggestion.unit || formData.unit
    });
    setShowSuggestions(false);
    setItemSuggestions([]);
  };

  // Filter RFQs based on search query (searches in all items including Item 2, 3, etc.)
  const filteredRfqs = useMemo(() => {
    let filtered = rfqs;
    
    // Filter by status if specified
    const params = new URLSearchParams(location.search);
    const statusFilter = params.get('status');
    if (statusFilter) {
      filtered = filtered.filter(rfq => rfq.status === statusFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(rfq => {
        // Search in RFQ number
        if (rfq.rfq_number.toLowerCase().includes(query)) return true;
        // Search in primary item name
        if (rfq.item_name.toLowerCase().includes(query)) return true;
        // Search in primary item description (item category)
        if (rfq.description && rfq.description.toLowerCase().includes(query)) return true;
        // Search in additional items (Item 2, 3, etc.)
        if (rfq.items && rfq.items.length > 0) {
          if (rfq.items.some(item => 
            (item.item_name && item.item_name.toLowerCase().includes(query)) ||
            (item.description && item.description.toLowerCase().includes(query))
          )) return true;
        }
        // Search in supplier categories (e.g., FLANGE, BEARING)
        if (rfq.supplier_categories && rfq.supplier_categories.length > 0) {
          if (rfq.supplier_categories.some(cat => cat.toLowerCase().includes(query))) return true;
        }
        // Search in supplier product types
        if (rfq.supplier_product_types && rfq.supplier_product_types.length > 0) {
          if (rfq.supplier_product_types.some(pt => pt.toLowerCase().includes(query))) return true;
        }
        return false;
      });
    }
    
    return filtered;
  }, [rfqs, searchQuery, location.search]);

  useEffect(() => {
    fetchRFQs();
    
    // Check if create=true in query params
    const params = new URLSearchParams(location.search);
    if (params.get('create') === 'true') {
      setShowDialog(true);
      
      // Check if there's PR data to pre-fill from conversion
      if (location.state?.prData) {
        const prData = location.state.prData;
        setPrConversion({
          prId: location.state.prId,
          prNumber: location.state.prNumber,
          prAttachments: prData.attachments || [] // Store PR attachments for transfer
        });
        
        // Pre-fill the form with PR data
        setFormData({
          item_name: prData.item_name || '',
          description: prData.description || '',
          quantity: prData.quantity?.toString() || '',
          unit: prData.unit || '',
          payment_term: prData.payment_term || '',
          transport: prData.transport || '',
          link_validity_days: 7,
        });
        
        // Pre-fill additional items if any
        if (prData.items && prData.items.length > 0) {
          setAdditionalItems(prData.items.map(item => ({
            id: item.id || `item_${Date.now()}_${Math.random()}`,
            item_name: item.item_name || '',
            description: item.description || '',
            quantity: item.quantity?.toString() || '',
            unit: item.unit || '',
            payment_term: item.payment_term || '',
            transport: item.transport || '',
            custom_fields: [],
            attachments: item.attachments || []
          })));
        }
        
        toast.info(`Converting ${location.state.prNumber} to RFQ`);
      }
    }
  }, [location]);

  const fetchRFQs = async () => {
    try {
      const response = await apiClient.get('/rfqs');
      setRfqs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching RFQs:', error);
      toast.error('Failed to load RFQs');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isCreating) return;
    setIsCreating(true);

    try {
      const response = await apiClient.post('/rfqs', {
        ...formData,
        quantity: parseFloat(formData.quantity),
        link_validity_days: parseInt(formData.link_validity_days),
        custom_fields: customFields.map(f => ({
          ...f,
          id: f.id,  // Ensure ID is included for validation
        })),
        items: additionalItems.map(item => ({
          ...item,
          custom_fields: (item.custom_fields || []).filter(f => f.type !== 'file').map(f => ({
            ...f,
            id: f.id,
          })),
          payment_term: item.payment_term || '',
          transport: item.transport || '',
        })),
        // Include PR attachments if converting from PR
        attachments: prConversion?.prAttachments || [],
      });
      
      const rfqId = response.data.id;
      
      // Upload files for Item 1 (primary item)
      for (const field of customFields) {
        if (field.type === 'file' && field.file) {
          const fileFormData = new FormData();
          fileFormData.append('file', field.file);
          await apiClient.post(`/rfqs/${rfqId}/upload-attachment`, fileFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      
      // Upload files for additional items (Item 2, 3, etc.)
      for (const item of additionalItems) {
        const fileFields = (item.custom_fields || []).filter(f => f.type === 'file' && f.file);
        for (const field of fileFields) {
          const fileFormData = new FormData();
          fileFormData.append('file', field.file);
          fileFormData.append('item_id', item.id);
          await apiClient.post(`/rfqs/${rfqId}/upload-item-attachment`, fileFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      
      // If this was a PR conversion, mark the PR as converted
      if (prConversion?.prId) {
        try {
          await apiClient.post(`/purchase-requisitions/${prConversion.prId}/mark-converted?rfq_id=${rfqId}`);
          toast.success(`${prConversion.prNumber} converted to RFQ successfully`);
        } catch (prErr) {
          console.error('Error marking PR as converted:', prErr);
          // Don't fail the whole operation, just log it
        }
        setPrConversion(null);
      } else {
        toast.success('RFQ created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      fetchRFQs();
    } catch (error) {
      console.error('Error creating RFQ:', error);
      toast.error('Failed to create RFQ');
    } finally {
      setIsCreating(false);
    }
  };

  // Additional items management
  const addItem = () => {
    setAdditionalItems([...additionalItems, {
      id: `item_${Date.now()}`,
      item_name: '',
      description: '',
      quantity: '',
      unit: '',
      payment_term: '',
      transport: '',
      custom_fields: [],
      attachments: [],
    }]);
  };

  const updateItem = (id, updates) => {
    setAdditionalItems(additionalItems.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  const removeItem = (id) => {
    setAdditionalItems(additionalItems.filter(item => item.id !== id));
  };

  // Delete RFQ (only if not sent to any supplier)
  const handleDeleteRFQ = async (rfqId, rfqNumber, sentCount) => {
    console.log('handleDeleteRFQ called:', rfqId, rfqNumber, sentCount);
    if (sentCount > 0) {
      toast.error(`Cannot delete RFQ ${rfqNumber} - it has been sent to ${sentCount} supplier(s)`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete RFQ ${rfqNumber}? This action cannot be undone.`)) {
      console.log('User cancelled deletion');
      return;
    }
    
    setDeleting(rfqId);
    try {
      console.log('Calling delete API:', rfqId);
      await apiClient.delete(`/rfqs/${rfqId}`);
      toast.success(`RFQ ${rfqNumber} deleted successfully`);
      fetchRFQs();
    } catch (error) {
      console.error('Error deleting RFQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete RFQ');
    } finally {
      setDeleting(null);
    }
  };

  // Item-specific custom field management
  const addItemCustomField = (itemId, type) => {
    setAdditionalItems(additionalItems.map(item => {
      if (item.id === itemId) {
        const newField = {
          id: `field_${Date.now()}`,
          type,
          label: '',
          value: '',
          options: type === 'dropdown' ? [] : undefined,
        };
        return { ...item, custom_fields: [...(item.custom_fields || []), newField] };
      }
      return item;
    }));
  };

  const updateItemCustomField = (itemId, fieldId, updates) => {
    setAdditionalItems(additionalItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          custom_fields: (item.custom_fields || []).map(field =>
            field.id === fieldId ? { ...field, ...updates } : field
          )
        };
      }
      return item;
    }));
  };

  const removeItemCustomField = (itemId, fieldId) => {
    setAdditionalItems(additionalItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          custom_fields: (item.custom_fields || []).filter(field => field.id !== fieldId)
        };
      }
      return item;
    }));
  };

  const addCustomField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: '',
      value: '',
      options: type === 'dropdown' ? [] : undefined,
      file: null,
    };
    setCustomFields([...customFields, newField]);
  };

  const updateCustomField = (id, updates) => {
    setCustomFields(customFields.map(field => 
      field.id === id ? { ...field, ...updates } : field
    ));
  };

  const removeCustomField = (id) => {
    setCustomFields(customFields.filter(field => field.id !== id));
  };

  const handleDownloadPDF = async (rfqId, rfqNumber) => {
    try {
      const response = await apiClient.get(`/comparison/${rfqId}/export?format=pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparison_${rfqNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Make sure there are quotes to compare.');
    }
  };

  // Copy RFQ functionality
  const handleCopyRFQ = async (rfqId, rfqNumber) => {
    setCopying(rfqId);
    try {
      const response = await apiClient.post(`/rfqs/${rfqId}/copy`);
      toast.success(`RFQ copied! New RFQ: ${response.data.rfq.rfq_number}`);
      fetchRFQs();
      // Navigate to the new RFQ
      navigate(`/rfqs/${response.data.rfq.id}`);
    } catch (error) {
      console.error('Error copying RFQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to copy RFQ');
    } finally {
      setCopying(null);
    }
  };

  // Progress Modal - fetch sent suppliers and their status
  const handleShowProgress = async (rfq) => {
    try {
      const sentRes = await apiClient.get(`/rfqs/${rfq.id}/sent-suppliers`);
      setProgressModalData({
        rfq,
        suppliers: sentRes.data
      });
      setShowProgressModal(true);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Failed to load progress details');
    }
  };

  // Send reminder to supplier
  const handleSendReminder = async (rfqId, supplierId) => {
    setSendingReminder(supplierId);
    try {
      await apiClient.post(`/rfqs/${rfqId}/send-reminder/${supplierId}`);
      toast.success('Reminder sent successfully');
      // Refresh the progress data
      if (progressModalData) {
        const sentRes = await apiClient.get(`/rfqs/${rfqId}/sent-suppliers`);
        setProgressModalData({
          ...progressModalData,
          suppliers: sentRes.data
        });
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const resetForm = () => {
    setFormData({
      item_name: '',
      description: '',
      quantity: '',
      unit: '',
      payment_term: '',
      transport: '',
      link_validity_days: 7,
    });
    setCustomFields([]);
    setAdditionalItems([]);
    setPrConversion(null);
  };

  // CSV upload for items
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        
        // Proper CSV parsing that handles quoted fields with newlines and commas
        const parseCSV = (csvText) => {
          const rows = [];
          let currentRow = [];
          let currentField = '';
          let insideQuotes = false;
          
          // Normalize line endings
          const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          
          for (let i = 0; i < normalizedText.length; i++) {
            const char = normalizedText[i];
            const nextChar = normalizedText[i + 1];
            
            if (char === '"') {
              if (insideQuotes && nextChar === '"') {
                // Escaped quote inside quoted field
                currentField += '"';
                i++; // Skip next quote
              } else {
                // Toggle quote state
                insideQuotes = !insideQuotes;
              }
            } else if (char === ',' && !insideQuotes) {
              // End of field
              currentRow.push(currentField.trim());
              currentField = '';
            } else if (char === '\n' && !insideQuotes) {
              // End of row
              currentRow.push(currentField.trim());
              if (currentRow.some(cell => cell !== '')) {
                rows.push(currentRow);
              }
              currentRow = [];
              currentField = '';
            } else {
              currentField += char;
            }
          }
          
          // Handle last field/row
          if (currentField || currentRow.length > 0) {
            currentRow.push(currentField.trim());
            if (currentRow.some(cell => cell !== '')) {
              rows.push(currentRow);
            }
          }
          
          return rows;
        };
        
        const rows = parseCSV(text);
        
        if (rows.length < 2) {
          toast.error('CSV must have a header row and at least one data row');
          return;
        }

        // Parse header - clean up quotes
        const headers = rows[0].map(h => h.toLowerCase().replace(/['"]/g, '').trim());
        const requiredHeaders = ['item_name', 'description', 'qty', 'uom'];
        
        // Check required headers
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(', ')}. Required: item_name, description, qty, uom. Optional: payment_term, transport`);
          return;
        }

        const itemNameIdx = headers.indexOf('item_name');
        const descIdx = headers.indexOf('description');
        const qtyIdx = headers.indexOf('qty');
        const uomIdx = headers.indexOf('uom');
        const paymentIdx = headers.indexOf('payment_term');
        const transportIdx = headers.indexOf('transport');

        const newItems = [];
        let isFirstItem = true;

        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].map(v => v.replace(/^["']|["']$/g, '').trim());
          
          if (!values[itemNameIdx]) continue;

          const itemData = {
            id: `item_${Date.now()}_${i}`,
            item_name: values[itemNameIdx] || '',
            description: values[descIdx] || '',
            quantity: values[qtyIdx] || '',
            unit: values[uomIdx] || '',
            payment_term: paymentIdx >= 0 ? (values[paymentIdx] || '') : '',
            transport: transportIdx >= 0 ? (values[transportIdx] || '') : '',
            custom_fields: [],
            attachments: [],
          };

          if (isFirstItem) {
            // Set first item as the primary item
            setFormData({
              ...formData,
              item_name: itemData.item_name,
              description: itemData.description,
              quantity: itemData.quantity,
              unit: itemData.unit,
              payment_term: itemData.payment_term,
              transport: itemData.transport,
            });
            isFirstItem = false;
          } else {
            newItems.push(itemData);
          }
        }

        setAdditionalItems([...additionalItems, ...newItems]);
        toast.success(`Imported ${newItems.length + 1} items from CSV`);
      } catch (error) {
        console.error('CSV parse error:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    resetForm();
    setIsCreating(false); // Reset creating state when dialog closes
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="rfqs-loading">Loading RFQs...</div>;
  }

  return (
    <div data-testid="rfqs-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="rfqs-title">RFQs</h1>
          <p className="text-sm text-slate-500 mt-2">Manage your requests for quotation</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="create-rfq-dialog-btn">
          <Plus size={18} />
          Create RFQ
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by RFQ, Item, Supplier Category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="rfq-search-input"
          />
        </div>
        <div className="flex items-center gap-4 mt-2">
          {(() => {
            const params = new URLSearchParams(location.search);
            const statusFilter = params.get('status');
            if (statusFilter) {
              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    Filtering by: <span className="font-semibold capitalize">{statusFilter}</span>
                  </span>
                  <button 
                    onClick={() => window.location.href = '/rfqs'}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Clear filter
                  </button>
                </div>
              );
            }
            return null;
          })()}
          {searchQuery && (
            <p className="text-sm text-slate-500">
              Showing {filteredRfqs.length} of {rfqs.length} RFQs
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="rfqs-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">RFQ Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quotes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredRfqs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-slate-500" data-testid="no-rfqs-message">
                    {searchQuery ? 'No RFQs match your search.' : 'No RFQs found. Create your first RFQ to get started.'}
                  </td>
                </tr>
              ) : (
                filteredRfqs.map((rfq) => {
                  const additionalItems = rfq.items || [];
                  const totalItems = 1 + additionalItems.length;
                  
                  return (
                  <tr key={rfq.id} className="hover:bg-slate-50" data-testid={`rfq-row-${rfq.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-medium text-slate-900">{rfq.rfq_number}</span>
                      {totalItems > 1 && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {totalItems} items
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {/* Item 1 */}
                      <div className="text-sm font-medium text-slate-900">
                        <span className="text-blue-600 font-semibold">1.</span> {rfq.item_name}
                        <span className="text-slate-500 font-mono text-xs ml-2">({rfq.quantity} {rfq.unit})</span>
                      </div>
                      {/* Additional Items */}
                      {additionalItems.map((item, idx) => (
                        <div key={item.id} className="text-sm text-slate-700 mt-1">
                          <span className="text-green-600 font-semibold">{idx + 2}.</span> {item.item_name}
                          <span className="text-slate-500 font-mono text-xs ml-2">({item.quantity} {item.unit})</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`badge ${
                          rfq.status === 'sent'
                            ? 'badge-info'
                            : rfq.status === 'closed'
                            ? 'badge-success'
                            : 'badge-warning'
                        }`}
                        data-testid={`rfq-status-${rfq.id}`}
                      >
                        {rfq.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {rfq.sent_count > 0 ? (
                        <div 
                          className="w-28 cursor-pointer hover:opacity-80"
                          onClick={() => handleShowProgress(rfq)}
                          title="Click to view submission details"
                          data-testid={`progress-bar-${rfq.id}`}
                        >
                          <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>{rfq.received_count} of {rfq.sent_count}</span>
                            <span>{Math.round((rfq.received_count / rfq.sent_count) * 100)}%</span>
                          </div>
                          <Progress value={Math.round((rfq.received_count / rfq.sent_count) * 100)} className="h-2" />
                          <p className="text-xs text-blue-600 mt-1">Click for details</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not sent</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {new Date(rfq.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {rfq.expires_at
                        ? new Date(rfq.expires_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Link to={`/rfqs/${rfq.id}`} data-testid={`view-rfq-detail-${rfq.id}`}>
                          <button className="text-sm text-slate-900 font-medium hover:text-slate-700">
                            {rfq.status === 'draft' ? 'Send' : 'View'} →
                          </button>
                        </Link>
                        {rfq.received_count > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPDF(rfq.id, rfq.rfq_number)}
                            className="text-xs h-7 px-2"
                            title="Download Quote Comparison PDF"
                          >
                            <Download size={12} className="mr-1" />
                            PDF
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCopyRFQ(rfq.id, rfq.rfq_number)}
                          disabled={copying === rfq.id}
                          className="text-xs h-7 px-2"
                          title="Copy RFQ"
                          data-testid={`copy-rfq-${rfq.id}`}
                        >
                          <Copy size={12} className="mr-1" />
                          {copying === rfq.id ? '...' : 'Copy'}
                        </Button>
                        {rfq.sent_count === 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteRFQ(rfq.id, rfq.rfq_number, rfq.sent_count)}
                            disabled={deleting === rfq.id}
                            className="text-xs h-7 px-2 text-red-600 border-red-200 hover:bg-red-50"
                            title="Delete RFQ"
                            data-testid={`delete-rfq-${rfq.id}`}
                          >
                            <Trash2 size={12} className="mr-1" />
                            {deleting === rfq.id ? '...' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-rfq-dialog">
          <DialogHeader>
            <DialogTitle>
              {prConversion ? `Convert ${prConversion.prNumber} to RFQ` : 'Create New RFQ'}
            </DialogTitle>
            <DialogDescription>
              {prConversion 
                ? `Creating RFQ from Purchase Requisition ${prConversion.prNumber}. Review and modify the details below.`
                : 'Fill in the details for the request for quotation. You can add multiple items.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* PR Conversion Notice */}
          {prConversion && (
            <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
              <ClipboardList size={20} className="text-purple-600" />
              <span className="text-sm text-purple-800">
                Converting from <strong>{prConversion.prNumber}</strong> - Items pre-filled from PR
              </span>
            </div>
          )}
          
          {/* CSV Upload Section */}
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-green-600" />
              <span className="text-sm text-green-800">Bulk import items from CSV</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleCSVUpload}
                data-testid="csv-upload-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                className="text-green-700 border-green-300 hover:bg-green-100"
                onClick={() => csvInputRef.current?.click()}
                data-testid="csv-upload-btn"
              >
                <Upload size={16} />
                Upload CSV
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-green-700 hover:bg-green-100 underline"
                onClick={async () => {
                  try {
                    const response = await apiClient.get('/items/sample-csv', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'sample_items.csv');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Sample CSV downloaded');
                  } catch (error) {
                    toast.error('Failed to download sample CSV');
                  }
                }}
                data-testid="download-sample-csv-btn"
              >
                <Download size={14} className="mr-1" />
                Sample CSV
              </Button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Item 1 (Primary) - Same layout as Item 2 */}
            <div className="p-4 border-2 rounded-lg bg-blue-50 border-blue-300">
              <div className="flex justify-between items-start mb-3">
                <span className="text-lg font-semibold text-blue-800">
                  📦 Item #1 (Primary)
                </span>
              </div>
              
              {/* Item 1 Basic Info */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="relative">
                  <Input
                    placeholder="Item Name * (type to search history)"
                    data-testid="rfq-item-input"
                    value={formData.item_name}
                    onChange={(e) => handleItemNameChange(e.target.value)}
                    onFocus={() => formData.item_name.length >= 2 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    autoComplete="off"
                    required
                    className="bg-white"
                  />
                  {showSuggestions && itemSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {itemSuggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
                          onMouseDown={() => selectSuggestion(suggestion)}
                        >
                          <div className="text-sm font-medium text-slate-900">{suggestion.item_name}</div>
                          <div className="text-xs text-slate-500">{suggestion.description} | {suggestion.unit}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Input
                  placeholder="Description *"
                  data-testid="rfq-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="bg-white"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Quantity *"
                  data-testid="rfq-quantity-input"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  className="bg-white"
                />
                <Input
                  placeholder="Unit (e.g., Pcs, Kg)"
                  data-testid="rfq-unit-input"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  required
                  className="bg-white"
                />
                <Input
                  placeholder="Payment Term (e.g., Net 30, Advance)"
                  data-testid="rfq-payment-term-input"
                  value={formData.payment_term}
                  onChange={(e) => setFormData({ ...formData, payment_term: e.target.value })}
                  className="bg-white"
                />
                <Input
                  placeholder="Transport (e.g., Ex-Works, Delivered)"
                  data-testid="rfq-transport-input"
                  value={formData.transport}
                  onChange={(e) => setFormData({ ...formData, transport: e.target.value })}
                  className="bg-white"
                />
              </div>
              
              {/* Item 1 Mandatory Requirements */}
              <div className="bg-white p-3 rounded border border-blue-200 mb-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-blue-700">
                    Mandatory Requirements for Item #1
                  </span>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => addCustomField('text')} data-testid="add-text-field">
                      + Text
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => addCustomField('dropdown')} data-testid="add-dropdown-field">
                      + Dropdown
                    </Button>
                  </div>
                </div>
                
                {customFields.filter(f => f.type !== 'file').map((field, index) => (
                  <div key={field.id} className="p-2 bg-amber-50 rounded mb-2 border border-amber-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-amber-700">
                        {field.type === 'dropdown' ? 'Dropdown' : 'Text'} #{index + 1}
                      </span>
                      <button type="button" onClick={() => removeCustomField(field.id)} className="text-red-500 text-xs">
                        Remove
                      </button>
                    </div>
                    <Input
                      placeholder="Question/Requirement"
                      value={field.label}
                      onChange={(e) => updateCustomField(field.id, { label: e.target.value })}
                      className="mb-1 text-sm h-8"
                    />
                    {field.type === 'dropdown' && (
                      <Input
                        placeholder="Options (comma-separated)"
                        value={field.options ? field.options.join(', ') : ''}
                        onChange={(e) => updateCustomField(field.id, { options: e.target.value.split(',').map(o => o.trim()) })}
                        className="text-sm h-8"
                      />
                    )}
                  </div>
                ))}
                
                {customFields.filter(f => f.type !== 'file').length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-1">
                    No mandatory requirements for this item
                  </p>
                )}
              </div>
              
              {/* Item 1 Attachments */}
              <div className="bg-white p-3 rounded border border-blue-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-blue-700">
                    📎 Attachments for Item #1 (Informational)
                  </span>
                  <Button type="button" size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => addCustomField('file')} data-testid="add-file-field">
                    + Add File
                  </Button>
                </div>
                
                {/* Show PR Attachments if converting from PR */}
                {prConversion?.prAttachments && prConversion.prAttachments.length > 0 && (
                  <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded">
                    <span className="text-xs font-medium text-purple-700 block mb-2">
                      📁 Attachments from {prConversion.prNumber}:
                    </span>
                    {prConversion.prAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-purple-600 mb-1">
                        <span>• {att.filename || att.name || `File ${idx + 1}`}</span>
                        {att.url && (
                          <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                            View
                          </a>
                        )}
                      </div>
                    ))}
                    <p className="text-xs text-purple-500 mt-1 italic">These will be included in the RFQ</p>
                  </div>
                )}
                
                {customFields.filter(f => f.type === 'file').map((field, index) => (
                  <div key={field.id} className="p-2 bg-slate-50 rounded mb-2 border border-slate-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-slate-700">File #{index + 1}</span>
                      <button type="button" onClick={() => removeCustomField(field.id)} className="text-red-500 text-xs">
                        Remove
                      </button>
                    </div>
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) updateCustomField(field.id, { file, label: file.name });
                      }}
                      className="w-full text-xs"
                    />
                    {field.file && <p className="text-xs text-green-600 mt-1">Selected: {field.file.name}</p>}
                  </div>
                ))}
                
                {customFields.filter(f => f.type === 'file').length === 0 && (!prConversion?.prAttachments || prConversion.prAttachments.length === 0) && (
                  <p className="text-xs text-slate-400 text-center py-1">
                    No attachments for this item
                  </p>
                )}
              </div>
            </div>
            {/* Additional Items Section (Multi-Part RFQ) */}
            <div className="border-t pt-4">
              <div className="mb-3">
                <Label>Additional Items/Parts</Label>
                <p className="text-xs text-slate-500">Add more items to create a multi-part RFQ</p>
              </div>
              
              {additionalItems.length === 0 && (
                <p className="text-xs text-slate-500 text-center py-2 mb-3">
                  Click "Add Item" below to create a multi-part RFQ with multiple items
                </p>
              )}
              
              {additionalItems.map((item, index) => (
                <div key={item.id} className="p-4 border-2 rounded-lg mb-4 bg-green-50 border-green-300">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-lg font-semibold text-green-800">
                      📦 Item #{index + 2}
                    </span>
                    <button type="button" onClick={() => removeItem(item.id)} className="text-red-600 hover:text-red-800">
                      <Trash2 size={18} />
                    </button>
                  </div>
                  
                  {/* Item Basic Info */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Input
                      placeholder="Item Name *"
                      value={item.item_name}
                      onChange={(e) => updateItem(item.id, { item_name: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem(item.id, { description: e.target.value })}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Quantity *"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, { quantity: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Unit (e.g., Pcs, Kg)"
                      value={item.unit}
                      onChange={(e) => updateItem(item.id, { unit: e.target.value })}
                    />
                    <Input
                      placeholder="Payment Term (e.g., Net 30)"
                      value={item.payment_term || ''}
                      onChange={(e) => updateItem(item.id, { payment_term: e.target.value })}
                    />
                    <Input
                      placeholder="Transport (e.g., Ex-Works)"
                      value={item.transport || ''}
                      onChange={(e) => updateItem(item.id, { transport: e.target.value })}
                    />
                  </div>
                  
                  {/* Item-specific Custom Fields */}
                  <div className="bg-white p-3 rounded border border-green-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-green-700">
                        Mandatory Requirements for Item #{index + 2}
                      </span>
                      <div className="flex gap-1">
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          className="text-xs h-7 px-2"
                          onClick={() => addItemCustomField(item.id, 'text')}
                        >
                          + Text
                        </Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          className="text-xs h-7 px-2"
                          onClick={() => addItemCustomField(item.id, 'dropdown')}
                        >
                          + Dropdown
                        </Button>
                      </div>
                    </div>
                    
                    {(item.custom_fields || []).filter(f => f.type !== 'file').map((field, fieldIdx) => (
                      <div key={field.id} className="p-2 bg-amber-50 rounded mb-2 border border-amber-200">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-amber-700">
                            {field.type === 'dropdown' ? 'Dropdown' : 'Text'} #{fieldIdx + 1}
                          </span>
                          <button 
                            type="button" 
                            onClick={() => removeItemCustomField(item.id, field.id)} 
                            className="text-red-500 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                        <Input
                          placeholder="Question/Requirement"
                          value={field.label}
                          onChange={(e) => updateItemCustomField(item.id, field.id, { label: e.target.value })}
                          className="mb-1 text-sm h-8"
                        />
                        {field.type === 'dropdown' && (
                          <Input
                            placeholder="Options (comma-separated)"
                            value={field.options ? field.options.join(', ') : ''}
                            onChange={(e) => updateItemCustomField(item.id, field.id, { options: e.target.value.split(',').map(o => o.trim()) })}
                            className="text-sm h-8"
                          />
                        )}
                      </div>
                    ))}
                    
                    {(!item.custom_fields || item.custom_fields.filter(f => f.type !== 'file').length === 0) && (
                      <p className="text-xs text-slate-400 text-center py-1">
                        No mandatory requirements for this item
                      </p>
                    )}
                  </div>
                  
                  {/* Item-specific Attachments */}
                  <div className="bg-white p-3 rounded border border-blue-200 mt-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-blue-700">
                        📎 Attachments for Item #{index + 2}
                      </span>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline"
                        className="text-xs h-7 px-2"
                        onClick={() => addItemCustomField(item.id, 'file')}
                      >
                        + Add File
                      </Button>
                    </div>
                    
                    {/* Show PR Attachments for this item if converting from PR */}
                    {item.attachments && item.attachments.length > 0 && (
                      <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded">
                        <span className="text-xs font-medium text-purple-700 block mb-2">
                          📁 Attachments from PR:
                        </span>
                        {item.attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-purple-600 mb-1">
                            <span>• {att.filename || att.name || `File ${idx + 1}`}</span>
                            {att.url && (
                              <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                View
                              </a>
                            )}
                          </div>
                        ))}
                        <p className="text-xs text-purple-500 mt-1 italic">These will be included in the RFQ</p>
                      </div>
                    )}
                    
                    {(item.custom_fields || []).filter(f => f.type === 'file').map((field, fileIdx) => (
                      <div key={field.id} className="p-2 bg-blue-50 rounded mb-2 border border-blue-200">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-blue-700">File #{fileIdx + 1}</span>
                          <button 
                            type="button" 
                            onClick={() => removeItemCustomField(item.id, field.id)} 
                            className="text-red-500 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              updateItemCustomField(item.id, field.id, { file, label: file.name });
                            }
                          }}
                          className="w-full text-xs"
                        />
                        {field.file && <p className="text-xs text-green-600 mt-1">Selected: {field.file.name}</p>}
                      </div>
                    ))}
                    
                    {(!item.custom_fields || item.custom_fields.filter(f => f.type === 'file').length === 0) && (!item.attachments || item.attachments.length === 0) && (
                      <p className="text-xs text-slate-400 text-center py-1">
                        No attachments for this item
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Add Item Button - At the bottom */}
              <div className="flex justify-center mt-4 mb-2">
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  onClick={addItem} 
                  data-testid="add-item-btn"
                  className="border-dashed border-2 border-green-400 text-green-700 hover:bg-green-50"
                >
                  <Plus size={14} className="mr-1" />
                  Add Item #{additionalItems.length + 2}
                </Button>
              </div>
            </div>
            
            {/* Link Validity - At the bottom */}
            <div className="border-t pt-4 bg-slate-50 p-4 rounded-lg">
              <Label htmlFor="link_validity_days">Link Validity (Days) *</Label>
              <Input
                id="link_validity_days"
                type="number"
                min="1"
                data-testid="rfq-validity-input"
                value={formData.link_validity_days}
                onChange={(e) => setFormData({ ...formData, link_validity_days: e.target.value })}
                required
                className="mt-2 bg-white"
              />
              <p className="text-xs text-slate-500 mt-1">How long should the supplier quote link remain active?</p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={isCreating} data-testid="cancel-rfq-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating} data-testid="save-rfq-btn">
                {isCreating ? 'Creating RFQ...' : 'Create RFQ'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Progress Modal - Show supplier submission status */}
      <Dialog open={showProgressModal} onOpenChange={setShowProgressModal}>
        <DialogContent className="max-w-2xl" data-testid="progress-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={20} />
              Quote Submission Progress
            </DialogTitle>
            <DialogDescription>
              {progressModalData?.rfq?.rfq_number} - {progressModalData?.rfq?.item_name}
            </DialogDescription>
          </DialogHeader>
          
          {progressModalData && (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {progressModalData.suppliers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No suppliers have been sent this RFQ yet.
                </div>
              ) : (
                <>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{progressModalData.suppliers.length}</div>
                      <div className="text-xs text-slate-500">Sent</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {progressModalData.suppliers.filter(s => s.status === 'submitted').length}
                      </div>
                      <div className="text-xs text-slate-500">Submitted</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-amber-600">
                        {progressModalData.suppliers.filter(s => s.status !== 'submitted').length}
                      </div>
                      <div className="text-xs text-slate-500">Pending</div>
                    </div>
                  </div>

                  {/* Supplier List */}
                  <div className="space-y-2">
                    {progressModalData.suppliers.map((supplier) => (
                      <div
                        key={supplier.supplier_id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          supplier.status === 'submitted'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {supplier.status === 'submitted' ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <Clock size={18} className="text-amber-600" />
                          )}
                          <div>
                            <div className="font-medium text-slate-900 text-sm">{supplier.supplier_name}</div>
                            <div className="text-xs text-slate-500">{supplier.supplier_email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            supplier.status === 'submitted'
                              ? 'bg-green-200 text-green-800'
                              : 'bg-amber-200 text-amber-800'
                          }`}>
                            {supplier.status === 'submitted' ? 'Submitted' : 'Pending'}
                          </span>
                          {supplier.status !== 'submitted' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendReminder(progressModalData.rfq.id, supplier.supplier_id)}
                              disabled={sendingReminder === supplier.supplier_id}
                              className="text-xs h-7 px-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                              data-testid={`send-reminder-${supplier.supplier_id}`}
                            >
                              <Bell size={12} className="mr-1" />
                              {sendingReminder === supplier.supplier_id ? 'Sending...' : 'Remind'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowProgressModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RFQs;